import { Form, Input, Select, Radio, Button, Tooltip, Space, Flex } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ICreateLedger } from "@shared/lib/types/briefCampains";
import InputNumber from "@shared/ui/InputNumber";

const { Option } = Select;

export function LedgerEntryForm({
  initialValues,
  onSubmit,
  isSubmitting,
  onCancel,
}: {
  initialValues?: Partial<ICreateLedger>;
  onSubmit?: (values: ICreateLedger) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
}) {
  const [form] = Form.useForm();

  const baseCost = Form.useWatch("baseCost", form) || 0;
  const tdsPercentage = Form.useWatch("tdsPercentage", form) ?? 10;
  const hasTds = Form.useWatch("hasTds", form) ?? true;

  const gstPercentage = Form.useWatch("gstPercentage", form) ?? 18;
  const hasGst = Form.useWatch("hasGst", form) ?? true;

  const gstValue = hasGst ? Math.round((baseCost * gstPercentage) / 100) : 0;
  const tdsValue = hasTds ? Math.round((baseCost * tdsPercentage) / 100) : 0;
  const maxPayout = baseCost - tdsValue + gstValue;

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...initialValues,
        hasGst: initialValues?.hasGst ?? true,
        hasTds: initialValues?.hasTds ?? true,
        gstPercentage: initialValues?.gstPercentage ?? 18,
        tdsPercentage: initialValues?.tdsPercentage ?? 10,
      }}
      onFinish={onSubmit}
    >
      <Form.Item
        label={
          <span>
            Choose Type
            <Tooltip
              title={
                <Flex vertical gap={4}>
                  <p>
                    <strong>Debit:</strong> Used when cost increases e.g.,
                    purchasing an influencer. Money will eventually go to the
                    influencer. Adds to amount payable.
                  </p>
                  <p>
                    <strong>Credit:</strong> Used when cost decreases — e.g.,
                    cancellation or return. Money is recovered or deducted from
                    influencer. Reduces amount payable.
                  </p>
                </Flex>
              }
            >
              <InfoCircleOutlined style={{ marginLeft: 6 }} />
            </Tooltip>
          </span>
        }
        name="type"
        rules={[{ required: true, message: "Please select type" }]}
      >
        <Select placeholder="Debit or Credit?" disabled={isSubmitting}>
          <Option value="Debit">
            <Space>
              Debit
              <Tooltip
                title="Used when cost increases e.g.,
                    purchasing an influencer. Money will eventually go to the
                    influencer. Adds to amount payable."
              >
                {" "}
                <InfoCircleOutlined color="#989898" style={{ fontSize: 12 }} />
              </Tooltip>
            </Space>
          </Option>
          <Option value="Credit">
            <Space>
              Credit
              <Tooltip
                title="Used when cost decreases — e.g.,
                    cancellation or return. Money is recovered or deducted from
                    influencer. Reduces amount payable."
              >
                <InfoCircleOutlined color="#989898" style={{ fontSize: 12 }} />
              </Tooltip>
            </Space>
          </Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Remarks"
        name="remarks"
        rules={[{ required: true, message: "Please enter remarks" }]}
      >
        <Input placeholder="Remarks" disabled={isSubmitting} />
      </Form.Item>

      <Form.Item
        label="Total Base Amount"
        name="baseCost"
        rules={[{ required: true, message: "Please enter amount" }]}
      >
        <InputNumber
          placeholder="Enter Amount"
          style={{ width: "100%" }}
          type="number"
          disabled={isSubmitting}
        />
      </Form.Item>

      <Form.Item
        label={
          <span>
            Does the ledger have <b>GST</b> ?
          </span>
        }
        name="hasGst"
      >
        <Radio.Group disabled={isSubmitting}>
          <Radio value={false}>No</Radio>
          <Radio value={true}>Yes, it Is</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        shouldUpdate={(prev, curr) =>
          prev.baseCost !== curr.baseCost ||
          prev.gstPercentage !== curr.gstPercentage ||
          prev.hasGst !== curr.hasGst
        }
      >
        {() => {
          const baseCost = form.getFieldValue("baseCost") || 0;
          const gstPercentage = form.getFieldValue("gstPercentage") ?? 18;
          const hasGst = form.getFieldValue("hasGst") ?? true;
          const gstValue = hasGst
            ? Math.round((baseCost * gstPercentage) / 100)
            : 0;
          return (
            hasGst && (
              <Space style={{ width: "100%" }}>
                <Form.Item name="gstPercentage" noStyle>
                  <InputNumber
                    suffix="%"
                    style={{ width: "100%" }}
                    type="number"
                    disabled={isSubmitting}
                    addonAfter={gstValue}
                  />
                </Form.Item>
              </Space>
            )
          );
        }}
      </Form.Item>

      <Form.Item
        label={
          <span>
            Does the ledger have <b>TDS</b> ?
          </span>
        }
        name="hasTds"
      >
        <Radio.Group disabled={isSubmitting}>
          <Radio value={false}>No</Radio>
          <Radio value={true}>Yes, it Is</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        shouldUpdate={(prev, curr) =>
          prev.baseCost !== curr.baseCost ||
          prev.tdsPercentage !== curr.tdsPercentage ||
          prev.hasTds !== curr.hasTds
        }
      >
        {() => {
          const baseCost = form.getFieldValue("baseCost") || 0;
          const tdsPercentage = form.getFieldValue("tdsPercentage") ?? 10;
          const hasTds = form.getFieldValue("hasTds") ?? true;
          const tdsValue = hasTds
            ? Math.round((baseCost * tdsPercentage) / 100)
            : 0;
          return (
            hasTds && (
              <Space style={{ width: "100%" }}>
                <Form.Item name="tdsPercentage" noStyle>
                  <InputNumber
                    suffix="%"
                    style={{ width: "100%" }}
                    type="number"
                    disabled={isSubmitting}
                    addonAfter={tdsValue}
                  />
                </Form.Item>
              </Space>
            )
          );
        }}
      </Form.Item>

      <Form.Item
        label={
          <span>
            Maximum Payout Amount
            <Tooltip
              title="The highest amount you can pay the creator now.
Calculated as: Base Amount - TDS + GST (if applicable)."
            >
              <InfoCircleOutlined style={{ marginLeft: 6 }} />
            </Tooltip>
          </span>
        }
      >
        <Input placeholder="Max Payout Amount" disabled value={maxPayout} />
      </Form.Item>

      <Form.Item>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
